/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The one order of names on this site: code-point order of the FOLDED name
 * (lowercase, diacritics stripped), ties broken by the code-point order of
 * the name itself. Deterministic and the same in every runtime.
 *
 * Never `localeCompare` for anything a page is baked with: the bake sorts with
 * Node's ICU and the hydrating browser with its own, and where the two
 * disagree React throws the baked tree away (#418) — or the bake and the
 * browser simply pick different things (the head-to-head pairs). Folding keeps
 * a lowercase or accented name among its letter (`go-e` with the G's,
 * `Évolt` with the E's), which a plain code-point sort would push past `Z`.
 *
 * Readers: the laadpalen catalog's brand order (compareChargerBrandNames in
 * src/lib/chargerCatalog.js, which the MID register's brand groups and the
 * portal's charge-point picker use too), the /about team (teamOrder in
 * src/lib/publicAuthors.js), the head-to-head ranking and pair order
 * (src/lib/headToHead.js), the aanbiedersoverzicht (neaOverviewRows in
 * src/lib/providerPayloads.js) and llms.txt's provider list, the NEa page's
 * compare picker, a profile page's "other providers" and the begrippenlijst's
 * tie-break (compareBegrippen in src/lib/begrippen.js). An identifier that
 * breaks a tie (an id) is compared as written, by compareCodePoints — the
 * article listing's compareNewestFirst and the begrippenlijst. The comparer's
 * name tie-break (src/lib/comparerSort.ts) keeps its own lowercase code-point
 * rule.
 *
 * Plain ESM JavaScript with zero imports, so the bake, the server and the
 * client load the same function and chargerCatalog.js may import it.
 */

/**
 * A name as the order compares it: lowercase, diacritics stripped.
 *
 * @param {unknown} text
 * @returns {string}
 */
export function foldName(text) {
  return String(text ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
}

/** @param {string} a @param {string} b */
const byCodePoint = (a, b) => (a < b ? -1 : a > b ? 1 : 0);

/**
 * Compare two identifiers (an id, a slug) as written, by code point — the
 * last tie-break of a list, where two names are equal. Not folded: an id is no
 * name, and two ids that differ only in case are two ids.
 *
 * @param {unknown} a
 * @param {unknown} b
 * @returns {number}
 */
export function compareCodePoints(a, b) {
  return byCodePoint(String(a ?? ''), String(b ?? ''));
}

/**
 * Compare two names: folded first, then as written.
 *
 * @param {unknown} a
 * @param {unknown} b
 * @returns {number}
 */
export function compareNames(a, b) {
  return byCodePoint(foldName(a), foldName(b)) || compareCodePoints(a, b);
}
